
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Part, PartFilter, PartCategory, PartSubcategory } from "@/data/parts";
import { usePartsFilter } from "@/hooks/use-parts-filter";
import ExpandableDescription from "@/components/ExpandableDescription";
import PartLinks from "@/components/PartLinks";

import PartCardHeader from "@/components/part-card/PartCardHeader";
import { useCollapsible } from "@/components/part-card/collapsible-context/CollapsibleContext";
import PartCardMetrics from "@/components/part-card/PartCardMetrics";
import PartCardImage from "@/components/part-card/PartCardImage";
import PartCardFilters from "@/components/part-card/PartCardFilters";
import PartCardSubcategory from "@/components/part-card/PartCardSubcategory";
import PartCardFooter from "@/components/part-card/PartCardFooter";

type SortOption = "none" | "rating-high" | "rating-low";

interface PartCardProps {
  part: Part;
  activeFilters: PartFilter[];
  sortBy?: SortOption;
  onRatingClick?: () => void;
  onHeartClick?: (partId: number) => void;
}

const PartCard = ({ 
  part, 
  activeFilters, 
  sortBy = "none", 
  onRatingClick, 
  onHeartClick 
}: PartCardProps) => {
  const navigate = useNavigate();
  const { isOpen } = useCollapsible();

  // Function to navigate to part details
  const goToPartDetails = () => {
    navigate(`/parts/${part.id}`);
  };

  // Get filter functions from usePartsFilter hook
  const { setSelectedCategory, setSelectedSubcategory, toggleFilter } = usePartsFilter();
  
  // Keep track of window resizing
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  
  // Effect to handle window resizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle category badge click
  const handleCategoryClick = (e: React.MouseEvent, category: PartCategory) => {
    e.stopPropagation();
    setSelectedCategory(category);
  };

  // Handle subcategory click
  const handleSubcategoryClick = (e: React.MouseEvent, subcategory: PartSubcategory) => {
    e.stopPropagation();
    setSelectedSubcategory(subcategory);
  };

  // Determine if the part has shopping links
  const hasShoppingLinks = !!(part.amazonLink || part.aliExpressLink);

  return (
    <Card className="h-full flex flex-col min-w-0 w-full bg-surface border-border overflow-hidden">
      <CardContent className="pt-6 flex flex-col h-full overflow-hidden">
        <PartCardHeader 
          part={part} 
          handleCategoryClick={handleCategoryClick}
          goToPartDetails={goToPartDetails}
        />
        
        {/* Image above the metrics row */}
        <PartCardImage 
          imageUrl={part.imageUrl} 
          name={part.name} 
        />
        
        <PartCardMetrics 
          rating={part.rating}
          subcategory={part.subcategory}
          sortBy={sortBy}
          onRatingClick={onRatingClick}
          requiresRoot={part.requiresRoot}
          requiresWiring={part.requiresWiring}
          requiresPrinting={part.requiresPrinting}
          requiresTools={part.requiresTools}
          requiresPurchase={part.requiresPurchase}
          printedParts={part.printedParts}
          hasShoppingLinks={hasShoppingLinks}
          filters={part.filters}
          onFilterToggle={toggleFilter}
          activeFilters={activeFilters}
        />
        
        <PartCardFilters 
          filters={part.filters}
          activeFilters={activeFilters}
          toggleFilter={toggleFilter}
        />
        
        <PartCardSubcategory 
          subcategory={part.subcategory}
          handleSubcategoryClick={handleSubcategoryClick}
        />
        
        <div className="text-foreground">
          <ExpandableDescription description={part.description} />
        </div>
        
        <div className="mt-auto mb-2">
          {/* Group guide links in one container */}
          <div className="mb-2">
            <PartLinks 
              videoGuide={part.videoGuide}
              writtenGuide={part.writtenGuide}
              amazonLink={null}
              aliExpressLink={null}
              printedParts={null}
            />
          </div>
          
          {/* Group shopping and printed parts links in another container */}
          <div>
            <PartLinks 
              amazonLink={part.amazonLink}
              aliExpressLink={part.aliExpressLink}
              printedParts={part.printedParts}
              videoGuide={null}
              writtenGuide={null}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <PartCardFooter 
          partId={part.id}
          onMoreInfoClick={goToPartDetails}
          externalOnHeartClick={onHeartClick}
        />
      </CardFooter>
    </Card>
  );
};

export default PartCard;
